import React, { useContext } from 'react';
import {
  Grid, Badge, LinkAsButton, Price as StrikeTrough,
} from '@printdeal/ui-library';
import { FormattedMessage } from 'react-intl';
import {
  SelectorContent,
  VatModeContext,
  useSelectorMachineContext,
  useSelectorMachineSend,
  Price,
  Selection,
  getGroupFromUrl,
  getSelectionFromUrl,
} from '@printdeal/product-selector';
import { dequal } from 'dequal';
import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { IProductRoute } from '../../../../types/product/productDetailPage';
import { getBadgeKind } from '../helpers/AttributeValueHelper';
import { pricePivotAttributes } from '../../../../helpers/PricesHelper';
import { Money } from '../../../Money';

interface ProductRouteItemProps {
  selector: SelectorContent;
  productRoute: IProductRoute;
  price: Price;
  originalPrice: Price;
}

export interface ProductRouteItem { productRoute: IProductRoute, price: Price, originalPrice: Price }

export const isSelectionEqual = (routeSelection: Selection, selectorSelection: Selection) => dequal(
  routeSelection,
  selectorSelection,
);

export const ProductRoute = ({
  selector, productRoute, price, originalPrice,
}: ProductRouteItemProps) => {
  const { vatMode } = useContext(VatModeContext);
  const selectorSelection = useSelectorMachineContext((state) => state.context.selection);
  const selectorMachineSend = useSelectorMachineSend();
  const singleSelection = getSelectionFromUrl(
    productRoute.productCode?.internal.content, selector.product.attributes,
  );

  if (!singleSelection) return null;

  const groupSelection = getGroupFromUrl(productRoute.productCode?.internal.content);
  const isActiveProductRoute = isSelectionEqual(singleSelection, selectorSelection);
  const displayedQuantity = Number(singleSelection.Quantity);
  if (singleSelection && groupSelection.length > 0) {
    singleSelection.groupedQuantity = String(displayedQuantity);
  }

  const handleProductRouteClick = () => {
    const touchedAttributes = Object.keys(singleSelection || {})
      .filter((attr) => !pricePivotAttributes.includes(attr));

    selectorMachineSend({
      type: 'Select product route',
      selection: singleSelection,
      groupSelection,
      totalQuantity: displayedQuantity,
      touchedAttributes,
    });

    addDataLayerEntry(
      'product_route_click',
      {
        id: productRoute.id,
        type: productRoute.label.type,
        selection: singleSelection,
      },
    );
  };

  return (
    <Grid className="pd-grid-cols-2 pd-pt-3 sm:pd-grid-cols-4">
      {price && (
        <>
          <Grid.Item className="pd-text-xs">
            <Badge variant={getBadgeKind(productRoute.label.type)}>
              {productRoute.label.displayName}
            </Badge>
          </Grid.Item>
          <Grid.Item className="pd-text-right pd-text-xs pd-text-dim-500 sm:pd-col-span-2">
            {productRoute?.productInfo && `${productRoute.productInfo},`}
            <FormattedMessage
              id="productConfigurator.productRoute.amountOfPiecesForPrice"
              values={{
                amount: displayedQuantity,
                price: (
                  <div className="pd-inline pd-lowercase">
                    {price[vatMode].centAmount !== originalPrice[vatMode].centAmount && (
                      <div className="pd-mx-1 pd-inline-block pd-text-right pd-text-dim-500">
                        <StrikeTrough variant="strikethrough">
                          <Money value={originalPrice[vatMode].centAmount} />
                        </StrikeTrough>
                      </div>
                    )}
                    <b className="pd-mx-1 pd-inline-block pd-text-right pd-text-dim-800">
                      <Money value={price[vatMode].centAmount} />
                    </b>
                    {vatMode === 'totalNet' && (
                    <FormattedMessage id="pricetable.pricemode.net" defaultMessage="Excl. VAT" />
                    )}
                    {vatMode === 'totalGross' && (
                    <FormattedMessage id="pricetable.pricemode.gross" defaultMessage="Incl. VAT" />
                    )}
                  </div>
                ),
              }}
              defaultMessage="{amount} {amount, plural, one {piece} other {pieces}} for {price}"
            />
          </Grid.Item>
          <Grid.Item className="pd-col-span-2 pd-text-right pd-text-xs pd-text-dim-500 sm:pd-col-span-1">
            {
            isActiveProductRoute
              ? (
                <FormattedMessage
                  id="productConfigurator.productRoute.selected"
                  defaultMessage="Selected"
                />
              )
              : (
                <LinkAsButton
                  variant="secondary"
                  id="view-product-route"
                  iconBefore="chevron-right"
                  onClick={handleProductRouteClick}
                >
                  <FormattedMessage
                    id="productConfigurator.productRoute.view"
                    defaultMessage="View"
                  />
                </LinkAsButton>
              )
          }
          </Grid.Item>
        </>
      )}
    </Grid>
  );
};
